import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Checkbox,
  Chip,
  Stack,
  Autocomplete,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import {
  useCreateService,
  useUpdateService,
  useAddLaborItem,
  useUpdateLaborItem,
  useDeleteLaborItem,
  useAddPartCategory,
  useUpdatePartCategory,
  useDeletePartCategory,
  useAddServiceSkill,
  useDeleteServiceSkill,
  useAddServiceExtra,
  useUpdateServiceExtra,
  useDeleteServiceExtra,
  useAddServiceLift,
  useDeleteServiceLift,
  type Service,
  type PricingType,
  type DurationType,
  type ServiceLaborItemTime,
} from '@/hooks/useServices';
import { LaborItemVehicleTimesDialog } from './LaborItemVehicleTimesDialog';
import { useAllSkills, type Skill } from '@/hooks/useSkills';
import { useCategories, type CategoryItem } from '@/hooks/useInventory';
import { useNotify } from '@/stores/notificationStore';
import { useAuthStore } from '@/stores/authStore';
import { eurosToCents } from '@/utils/money';
import { skillTypeGroupKey, toRequiredSkillOptions } from '@/utils/skillOptions';
import { LaborRateSelect } from '@/components/common/LaborRateSelect';

const MANAGE_SERVICES_PERMISSION = 'settings.manage_services';

interface ServiceFormDialogProps {
  open: boolean;
  onClose: () => void;
  editingService: Service | null;
}

type TabValue = 'basic' | 'labor' | 'extras' | 'skills' | 'equipment';

interface LaborItemDraft {
  id: string;
  description: string;
  estimatedHours: number;
  sortOrder: number;
  /** #3347: may be left out at creation, and whether it starts ticked. */
  isOptional: boolean;
  selectedByDefault: boolean;
  vehicleTimes: ServiceLaborItemTime[];
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface ExtraDraft {
  id: string;
  description: string;
  amountCents: number;
  amountInput: string;
  isTaxable: boolean;
  hasVariableQuantity: boolean;
  unit: string;
  sortOrder: number;
}

interface SkillDraft {
  id: string;
  skillId: string;
  skillName: string;
  minimumProficiency: string;
  isRequired: boolean;
}

interface PartCategoryDraft {
  id: string;
  partCategoryId: string;
  partCategoryName: string;
  isRequired: boolean;
  defaultQuantity: number;
  notes: string;
}

interface LiftDraft {
  id: string;
  liftType: string;
  liftTypeName: string;
  isRequired: boolean;
}

let draftIdCounter = 0;
function nextDraftId() {
  return `draft-${++draftIdCounter}`;
}

function serviceCatalogFlag(
  service: Service | null,
  key: 'requiresSignature' | 'requiresOwnershipCheck',
): boolean {
  if (service == null) return false;
  return (service as Service & Record<string, unknown>)[key] === true;
}

const proficiencyOptions = ['beginner', 'intermediate', 'expert'] as const;

const liftTypeOptions = [
  { value: '2_post', labelKey: 'lifts.type2Post' },
  { value: '4_post', labelKey: 'lifts.type4Post' },
  { value: 'scissor', labelKey: 'lifts.typeScissor' },
  { value: 'pit', labelKey: 'lifts.typePit' },
] as const;

export function ServiceFormDialog({ open, onClose, editingService }: ServiceFormDialogProps) {
  const { t } = useTranslation('settings');
  const notify = useNotify();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canManageServices = hasPermission(MANAGE_SERVICES_PERMISSION);

  const createService = useCreateService();
  const updateService = useUpdateService();
  const addLaborItemMut = useAddLaborItem();
  const updateLaborItemMut = useUpdateLaborItem();
  const deleteLaborItemMut = useDeleteLaborItem();
  const addPartCategoryMut = useAddPartCategory();
  const updatePartCategoryMut = useUpdatePartCategory();
  const deletePartCategoryMut = useDeletePartCategory();
  const addServiceSkill = useAddServiceSkill();
  const deleteServiceSkill = useDeleteServiceSkill();
  const addExtraMut = useAddServiceExtra();
  const updateExtraMut = useUpdateServiceExtra();
  const deleteExtraMut = useDeleteServiceExtra();
  const addServiceLift = useAddServiceLift();
  const deleteServiceLift = useDeleteServiceLift();
  const { data: allSkills } = useAllSkills();
  const { data: categoriesData } = useCategories();
  const categories = categoriesData?.data ?? [];

  const isEditing = !!editingService;
  const isLoading = createService.isPending || updateService.isPending;

  const [activeTab, setActiveTab] = useState<TabValue>('basic');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [pricingType, setPricingType] = useState<PricingType>('component');
  const [fixedPriceCents, setFixedPriceCents] = useState<number | null>(null);
  const [fixedPriceInput, setFixedPriceInput] = useState('');
  const [estimatedDurationHours, setEstimatedDurationHours] = useState<number | null>(null);
  const [durationType, setDurationType] = useState<DurationType>('fixed');
  const [durationOptions, setDurationOptions] = useState<(number | null)[]>([null, null]);
  const [isActive, setIsActive] = useState(true);
  const [laborRateId, setLaborRateId] = useState<string | null>(null);
  const [requiresSignature, setRequiresSignature] = useState(false);
  const [requiresOwnershipCheck, setRequiresOwnershipCheck] = useState(false);

  const [laborItems, setLaborItems] = useState<LaborItemDraft[]>([]);
  const [extras, setExtras] = useState<ExtraDraft[]>([]);
  const [skills, setSkills] = useState<SkillDraft[]>([]);
  const [partCategories, setPartCategories] = useState<PartCategoryDraft[]>([]);
  const [lifts, setLifts] = useState<LiftDraft[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [timesForItem, setTimesForItem] = useState<LaborItemDraft | null>(null);

  useEffect(() => {
    if (open) {
      if (editingService) {
        setName(editingService.name);
        setDescription(editingService.description ?? '');
        setPricingType(editingService.pricingType);
        setFixedPriceCents(editingService.fixedPriceCents);
        setFixedPriceInput(editingService.fixedPriceCents != null ? (editingService.fixedPriceCents / 100).toFixed(2) : '');
        setEstimatedDurationHours(editingService.estimatedDurationHours);
        setDurationType(editingService.durationType ?? 'fixed');
        setDurationOptions(
          editingService.durationOptions && editingService.durationOptions.length > 0
            ? editingService.durationOptions
            : [null, null]
        );
        setIsActive(editingService.isActive);
        setLaborRateId(editingService.laborRateId ?? null);
        setRequiresSignature(serviceCatalogFlag(editingService, 'requiresSignature'));
        setRequiresOwnershipCheck(serviceCatalogFlag(editingService, 'requiresOwnershipCheck'));
        setLaborItems(
          editingService.laborItems.map((li) => ({
            id: li.id,
            description: li.description,
            estimatedHours: li.estimatedHours,
            sortOrder: li.sortOrder,
            isOptional: li.isOptional,
            selectedByDefault: li.selectedByDefault,
            vehicleTimes: li.vehicleTimes ?? [],
          })),
        );
        setExtras(
          editingService.extras.map((ex) => ({
            id: ex.id,
            description: ex.description,
            amountCents: ex.amountCents,
            amountInput: (ex.amountCents / 100).toFixed(2),
            isTaxable: ex.isTaxable,
            hasVariableQuantity: ex.hasVariableQuantity ?? false,
            unit: ex.unit ?? '',
            sortOrder: ex.sortOrder,
          })),
        );
        setSkills(
          editingService.skills.map((sk) => ({
            id: sk.id,
            skillId: sk.skillId,
            skillName: sk.skillName ?? '',
            minimumProficiency: sk.minimumProficiency,
            isRequired: sk.isRequired,
          })),
        );
        setPartCategories(
          editingService.partCategories.map((pc) => ({
            id: pc.id,
            partCategoryId: pc.partCategoryId,
            partCategoryName: pc.partCategoryName ?? '',
            isRequired: pc.isRequired,
            defaultQuantity: pc.defaultQuantity ?? 1,
            notes: pc.notes ?? '',
          })),
        );
        setLifts(
          editingService.lifts.map((ll) => ({
            id: ll.id,
            liftType: ll.liftType,
            liftTypeName: ll.liftType,
            isRequired: ll.isRequired,
          })),
        );
      } else {
        setName('');
        setDescription('');
        setPricingType('component');
        setFixedPriceCents(null);
        setFixedPriceInput('');
        setEstimatedDurationHours(null);
        setDurationType('fixed');
        setDurationOptions([null, null]);
        setIsActive(true);
        setLaborRateId(null);
        setRequiresSignature(false);
        setRequiresOwnershipCheck(false);
        setLaborItems([]);
        setExtras([]);
        setSkills([]);
        setPartCategories([]);
        setLifts([]);
      }
      setActiveTab('basic');
      setError(null);
      setIsDirty(false);
    }
  }, [open, editingService]);

  const markDirty = () => setIsDirty(true);

  const handleSave = async () => {
    if (!hasPermission(MANAGE_SERVICES_PERMISSION)) return;
    setError(null);

    if (!name.trim()) {
      setError(t('servicesTab.nameRequired'));
      setActiveTab('basic');
      return;
    }

    try {
      const laborItemsPayload = laborItems.map((li, idx) => ({
        description: li.description,
        estimatedHours: li.estimatedHours,
        sortOrder: idx,
        isOptional: li.isOptional,
        selectedByDefault: li.selectedByDefault,
      }));
      const extrasPayload = extras.map((ex, idx) => ({
        description: ex.description,
        amountCents: ex.amountCents,
        isTaxable: ex.isTaxable,
        hasVariableQuantity: ex.hasVariableQuantity,
        unit: ex.hasVariableQuantity ? ex.unit || null : null,
        sortOrder: idx,
      }));
      const skillIdsPayload = skills.map((sk) => ({
        skillId: sk.skillId,
        minimumProficiency: sk.minimumProficiency,
        isRequired: sk.isRequired,
      }));
      const liftIdsPayload = lifts.map((lt) => ({
        liftType: lt.liftType,
        isRequired: lt.isRequired,
      }));
      const partCategoryIdsPayload = partCategories.map((pc) => ({
        partCategoryId: pc.partCategoryId,
        isRequired: pc.isRequired,
        defaultQuantity: pc.defaultQuantity,
        notes: pc.notes || null,
      }));

      const catalogFlags = { requiresSignature, requiresOwnershipCheck };

      if (isEditing) {
        await updateService.mutateAsync({
          id: editingService.id,
          data: {
            name: name.trim(),
            description: description.trim() || null,
            pricingType,
            fixedPriceCents: pricingType === 'fixed' ? fixedPriceCents : null,
            estimatedDurationHours: durationType === 'fixed' ? estimatedDurationHours : null,
            durationType,
            durationOptions: durationType === 'variable'
              ? durationOptions.filter((v): v is number => v != null && v > 0)
              : null,
            isActive,
            laborRateId,
            ...catalogFlags,
          },
        });

        const sid = editingService.id;

        // Sync labor items
        const origLaborIds = new Set(editingService.laborItems.map((li) => li.id));
        const currLaborIds = new Set(laborItems.map((li) => li.id));
        for (const orig of editingService.laborItems) {
          if (!currLaborIds.has(orig.id)) {
            await deleteLaborItemMut.mutateAsync({ serviceId: sid, itemId: orig.id });
          }
        }
        for (const [idx, li] of laborItems.entries()) {
          if (li.id.startsWith('draft-')) {
            await addLaborItemMut.mutateAsync({ serviceId: sid, data: { description: li.description, estimatedHours: li.estimatedHours, sortOrder: idx, isOptional: li.isOptional, selectedByDefault: li.selectedByDefault } });
          } else if (origLaborIds.has(li.id)) {
            await updateLaborItemMut.mutateAsync({ serviceId: sid, itemId: li.id, data: { description: li.description, estimatedHours: li.estimatedHours, sortOrder: idx, isOptional: li.isOptional, selectedByDefault: li.selectedByDefault } });
          }
        }

        // Sync extras
        const origExtraIds = new Set(editingService.extras.map((ex) => ex.id));
        const currExtraIds = new Set(extras.map((ex) => ex.id));
        for (const orig of editingService.extras) {
          if (!currExtraIds.has(orig.id)) {
            await deleteExtraMut.mutateAsync({ serviceId: sid, extraId: orig.id });
          }
        }
        for (const [idx, ex] of extras.entries()) {
          if (ex.id.startsWith('draft-')) {
            await addExtraMut.mutateAsync({ serviceId: sid, data: { description: ex.description, amountCents: ex.amountCents, isTaxable: ex.isTaxable, hasVariableQuantity: ex.hasVariableQuantity, unit: ex.hasVariableQuantity ? ex.unit || null : null, sortOrder: idx } });
          } else if (origExtraIds.has(ex.id)) {
            await updateExtraMut.mutateAsync({ serviceId: sid, extraId: ex.id, data: { description: ex.description, amountCents: ex.amountCents, isTaxable: ex.isTaxable, hasVariableQuantity: ex.hasVariableQuantity, unit: ex.hasVariableQuantity ? ex.unit || null : null, sortOrder: idx } });
          }
        }

        // Sync part categories
        const currPcIds = new Set(partCategories.map((pc) => pc.id));
        for (const orig of editingService.partCategories) {
          if (!currPcIds.has(orig.id)) {
            await deletePartCategoryMut.mutateAsync({ serviceId: sid, linkId: orig.id });
          }
        }
        for (const pc of partCategories) {
          if (pc.id.startsWith('draft-')) {
            await addPartCategoryMut.mutateAsync({ serviceId: sid, data: { partCategoryId: pc.partCategoryId, isRequired: pc.isRequired, defaultQuantity: pc.defaultQuantity, notes: pc.notes || null } });
            continue;
          }
          // An edit to an existing link used to be dropped (#3445).
          const orig = editingService.partCategories.find((o) => o.id === pc.id);
          if (
            orig &&
            (orig.isRequired !== pc.isRequired ||
              (orig.defaultQuantity ?? 1) !== pc.defaultQuantity ||
              (orig.notes ?? '') !== pc.notes)
          ) {
            await updatePartCategoryMut.mutateAsync({
              serviceId: sid,
              linkId: pc.id,
              data: { isRequired: pc.isRequired, defaultQuantity: pc.defaultQuantity, notes: pc.notes || null },
            });
          }
        }

        // Persist changes made in the Skills tab when editing an existing service.
        const currentSkillIds = new Set(skills.map((skill) => skill.id));
        for (const original of editingService.skills) {
          if (!currentSkillIds.has(original.id)) {
            await deleteServiceSkill.mutateAsync({ serviceId: sid, linkId: original.id });
          }
        }
        for (const skill of skills) {
          if (skill.id.startsWith('draft-')) {
            await addServiceSkill.mutateAsync({
              serviceId: sid,
              data: {
                skillId: skill.skillId,
                minimumProficiency: skill.minimumProficiency,
                isRequired: skill.isRequired,
              },
            });
          }
        }

        // Sync lift requirements
        const originalLifts = editingService.lifts;
        const currentLiftTypes = new Set(lifts.map((l) => l.liftType));
        const originalLiftTypes = new Set(originalLifts.map((l) => l.liftType));
        for (const orig of originalLifts) {
          if (!currentLiftTypes.has(orig.liftType)) {
            await deleteServiceLift.mutateAsync({ serviceId: sid, linkId: orig.id });
          }
        }
        for (const curr of lifts) {
          if (!originalLiftTypes.has(curr.liftType)) {
            await addServiceLift.mutateAsync({
              serviceId: sid,
              data: { liftType: curr.liftType, isRequired: curr.isRequired },
            });
          }
        }
      } else {
        await createService.mutateAsync({
          name: name.trim(),
          description: description.trim() || undefined,
          pricingType,
          fixedPriceCents: pricingType === 'fixed' ? fixedPriceCents : undefined,
          estimatedDurationHours: durationType === 'fixed' ? (estimatedDurationHours ?? undefined) : undefined,
          durationType,
          durationOptions: durationType === 'variable'
            ? durationOptions.filter((v): v is number => v != null && v > 0)
            : undefined,
          isActive,
          laborRateId,
          ...catalogFlags,
          laborItems: laborItemsPayload.length > 0 ? laborItemsPayload : undefined,
          extras: extrasPayload.length > 0 ? extrasPayload : undefined,
          skillIds: skillIdsPayload.length > 0 ? skillIdsPayload : undefined,
          liftIds: liftIdsPayload.length > 0 ? liftIdsPayload : undefined,
          partCategoryIds: partCategoryIdsPayload.length > 0 ? partCategoryIdsPayload : undefined,
        });
      }

      notify.success(t('servicesTab.saveSuccess'), t('servicesTab.saveSuccessDesc'));
      onClose();
    } catch {
      setError(t('servicesTab.saveFailed'));
    }
  };

  // Labor items helpers
  const handleAddLaborItem = () => {
    setLaborItems((prev) => [
      ...prev,
      { id: nextDraftId(), description: '', estimatedHours: 0.5, sortOrder: prev.length, isOptional: false, selectedByDefault: true, vehicleTimes: [] },
    ]);
    markDirty();
  };

  const handleRemoveLaborItem = (id: string) => {
    setLaborItems((prev) => prev.filter((li) => li.id !== id));
    markDirty();
  };

  const handleLaborItemChange = (id: string, field: keyof LaborItemDraft, value: string | number | boolean) => {
    setLaborItems((prev) =>
      prev.map((li) => (li.id === id ? { ...li, [field]: value } : li)),
    );
    markDirty();
  };

  // Extras helpers
  const handleAddExtra = () => {
    setExtras((prev) => [
      ...prev,
      { id: nextDraftId(), description: '', amountCents: 0, amountInput: '0.00', isTaxable: true, hasVariableQuantity: false, unit: '', sortOrder: prev.length },
    ]);
    markDirty();
  };

  const handleRemoveExtra = (id: string) => {
    setExtras((prev) => prev.filter((ex) => ex.id !== id));
    markDirty();
  };

  const handleExtraChange = (id: string, field: keyof ExtraDraft, value: string | number | boolean) => {
    setExtras((prev) =>
      prev.map((ex) => (ex.id === id ? { ...ex, [field]: value } : ex)),
    );
    markDirty();
  };

  // Skills helpers
  const handleAddSkill = (skill: Skill) => {
    if (skills.some((s) => s.skillId === skill.id)) return;
    setSkills((prev) => [
      ...prev,
      {
        id: nextDraftId(),
        skillId: skill.id,
        skillName: skill.name,
        minimumProficiency: 'beginner',
        isRequired: true,
      },
    ]);
    markDirty();
  };

  const handleRemoveSkill = (id: string) => {
    setSkills((prev) => prev.filter((s) => s.id !== id));
    markDirty();
  };

  const handleSkillChange = (id: string, field: keyof SkillDraft, value: string | boolean) => {
    setSkills((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
    markDirty();
  };

  // Part categories helpers
  const handleAddPartCategory = (cat: CategoryItem) => {
    if (partCategories.some((pc) => pc.partCategoryId === cat.id)) return;
    setPartCategories((prev) => [
      ...prev,
      {
        id: nextDraftId(),
        partCategoryId: cat.id,
        partCategoryName: cat.name,
        isRequired: true,
        defaultQuantity: 1,
        notes: '',
      },
    ]);
    markDirty();
  };

  const handleRemovePartCategory = (id: string) => {
    setPartCategories((prev) => prev.filter((pc) => pc.id !== id));
    markDirty();
  };

  const handlePartCategoryChange = (id: string, field: keyof PartCategoryDraft, value: string | boolean | number) => {
    setPartCategories((prev) =>
      prev.map((pc) => (pc.id === id ? { ...pc, [field]: value } : pc)),
    );
    markDirty();
  };

  // Lift helpers
  const handleAddLift = (option: { value: string; labelKey: string }) => {
    if (lifts.some((l) => l.liftType === option.value)) return;
    setLifts((prev) => [
      ...prev,
      {
        id: nextDraftId(),
        liftType: option.value,
        liftTypeName: option.labelKey,
        isRequired: true,
      },
    ]);
    markDirty();
  };

  const handleRemoveLift = (id: string) => {
    setLifts((prev) => prev.filter((l) => l.id !== id));
    markDirty();
  };

  const handleLiftChange = (id: string, field: keyof LiftDraft, value: string | boolean) => {
    setLifts((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
    );
    markDirty();
  };

  const availableLiftTypes = liftTypeOptions.filter(
    (opt) => !lifts.some((l) => l.liftType === opt.value),
  );

  const liftTypeLabel = (liftType: string) => {
    const opt = liftTypeOptions.find((o) => o.value === liftType);
    return opt ? t(opt.labelKey) : liftType;
  };

  const availableSkills = toRequiredSkillOptions(allSkills ?? []).filter(
    (s) => !skills.some((sk) => sk.skillId === s.id),
  );
  const availableCategories = categories.filter(
    (c) => !partCategories.some((pc) => pc.partCategoryId === c.id),
  );

  const proficiencyLabel = (level: string) => {
    switch (level) {
      case 'beginner': return t('servicesTab.proficiencyBeginner');
      case 'intermediate': return t('servicesTab.proficiencyIntermediate');
      case 'expert': return t('servicesTab.proficiencyExpert');
      default: return level;
    }
  };

  return (
    <>
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('servicesTab.editService') : t('servicesTab.addService')}
      isDirty={isDirty}
      onSubmit={() => void handleSave()}
      isLoading={isLoading}
      submitDisabled={!canManageServices}
      submitLabel={isEditing ? t('servicesTab.saveChanges') : t('servicesTab.createService')}
      submitIcon={<SaveIcon />}
      maxWidth="md"
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={activeTab} onChange={(_e, v) => setActiveTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab value="basic" label={t('servicesTab.basicInfoTab')} />
          <Tab
            value="labor"
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {t('servicesTab.laborTab')}
                {laborItems.length > 0 && <Chip size="small" label={laborItems.length} />}
              </Box>
            }
          />
          <Tab
            value="extras"
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {t('servicesTab.extrasTab')}
                {(extras.length + partCategories.length) > 0 && (
                  <Chip size="small" label={extras.length + partCategories.length} />
                )}
              </Box>
            }
          />
          <Tab
            value="skills"
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {t('servicesTab.skillsTab')}
                {skills.length > 0 && <Chip size="small" label={skills.length} />}
              </Box>
            }
          />
          <Tab
            value="equipment"
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {t('servicesTab.equipmentTab')}
                {lifts.length > 0 && <Chip size="small" label={lifts.length} />}
              </Box>
            }
          />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {activeTab === 'basic' && (
          <Stack spacing={3}>
            <TextField
              label={t('servicesTab.name')}
              value={name}
              onChange={(e) => { setName(e.target.value); markDirty(); }}
              fullWidth
              required
              autoFocus
              placeholder={t('servicesTab.namePlaceholder')}
            />

            <TextField
              label={t('servicesTab.description')}
              value={description}
              onChange={(e) => { setDescription(e.target.value); markDirty(); }}
              fullWidth
              multiline
              rows={2}
              placeholder={t('servicesTab.descriptionPlaceholder')}
            />

            <FormControl fullWidth>
              <InputLabel>{t('servicesTab.pricingType')}</InputLabel>
              <Select
                value={pricingType}
                label={t('servicesTab.pricingType')}
                onChange={(e) => { setPricingType(e.target.value as PricingType); markDirty(); }}
              >
                <MenuItem value="component">{t('servicesTab.pricingComponent')}</MenuItem>
                <MenuItem value="fixed">{t('servicesTab.pricingFixed')}</MenuItem>
              </Select>
            </FormControl>

            <LaborRateSelect
              value={laborRateId}
              onChange={(id) => { setLaborRateId(id); markDirty(); }}
              label={t('servicesTab.laborRate')}
              helperText={t('servicesTab.laborRateHint')}
              emptyLabel={t('servicesTab.laborRateWorkshopDefault')}
              disabled={!canManageServices}
            />

            {pricingType === 'fixed' && (
              <TextField
                label={t('servicesTab.fixedPrice')}
                type="number"
                value={fixedPriceInput}
                onChange={(e) => {
                  setFixedPriceInput(e.target.value);
                  markDirty();
                }}
                onBlur={() => {
                  const cents = eurosToCents(fixedPriceInput);
                  if (cents == null) {
                    setFixedPriceCents(null);
                    setFixedPriceInput('');
                  } else {
                    setFixedPriceCents(cents);
                    setFixedPriceInput((cents / 100).toFixed(2));
                  }
                }}
                fullWidth
                placeholder={t('servicesTab.fixedPricePlaceholder')}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">€</InputAdornment>,
                  },
                  htmlInput: { min: 0, step: '0.01' },
                }}
              />
            )}

            {/* Duration type toggle */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('servicesTab.durationLabel')}
              </Typography>
              <Box display="flex" gap={2} alignItems="center" mb={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={durationType === 'variable'}
                      onChange={(e) => {
                        setDurationType(e.target.checked ? 'variable' : 'fixed');
                        markDirty();
                      }}
                    />
                  }
                  label={t('servicesTab.variableDuration')}
                />
              </Box>

              {durationType === 'fixed' ? (
                <TextField
                  label={t('servicesTab.estimatedDurationHours')}
                  type="number"
                  value={estimatedDurationHours ?? ''}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value);
                    setEstimatedDurationHours(isNaN(val) ? null : val);
                    markDirty();
                  }}
                  fullWidth
                  placeholder={t('servicesTab.durationPlaceholder')}
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">h</InputAdornment>,
                    },
                    htmlInput: { min: 0, step: '0.5' },
                  }}
                />
              ) : (
                <Stack spacing={1}>
                  <Typography variant="caption" color="text.secondary">
                    {t('servicesTab.durationOptionsHint')}
                  </Typography>
                  {durationOptions.map((opt, index) => (
                    <Box key={index} display="flex" gap={1} alignItems="center">
                      <TextField
                        type="number"
                        value={opt ?? ''}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          const updated = [...durationOptions];
                          updated[index] = isNaN(val) ? null : val;
                          setDurationOptions(updated);
                          markDirty();
                        }}
                        size="small"
                        sx={{ width: 140 }}
                        placeholder={t('servicesTab.durationPlaceholder')}
                        slotProps={{
                          input: {
                            endAdornment: <InputAdornment position="end">h</InputAdornment>,
                          },
                          htmlInput: { min: 0, step: '0.5' },
                        }}
                      />
                      {durationOptions.length > 2 && (
                        <Tooltip title={t('servicesTab.removeDurationOption')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDurationOptions(durationOptions.filter((_, i) => i !== index));
                              markDirty();
                            }}
                            aria-label={t('servicesTab.removeDurationOption')}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setDurationOptions([...durationOptions, null]);
                      markDirty();
                    }}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {t('servicesTab.addDurationOption')}
                  </Button>
                </Stack>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => { setIsActive(e.target.checked); markDirty(); }}
                />
              }
              label={t('servicesTab.active')}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={requiresSignature}
                  onChange={(e) => { setRequiresSignature(e.target.checked); markDirty(); }}
                  slotProps={{ input: { 'aria-label': t('servicesTab.requiresSignature') } }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">{t('servicesTab.requiresSignature')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('servicesTab.requiresSignatureHint')}
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Switch
                  checked={requiresOwnershipCheck}
                  onChange={(e) => { setRequiresOwnershipCheck(e.target.checked); markDirty(); }}
                  slotProps={{ input: { 'aria-label': t('servicesTab.requiresOwnershipCheck') } }}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">{t('servicesTab.requiresOwnershipCheck')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('servicesTab.requiresOwnershipCheckHint')}
                  </Typography>
                </Box>
              }
            />
          </Stack>
        )}

        {activeTab === 'labor' && (
          <Stack spacing={2}>
            {laborItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('servicesTab.noLaborItems')}
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('servicesTab.laborItemDesc')}</TableCell>
                    <TableCell sx={{ width: 150 }}>{t('servicesTab.laborItemHours')}</TableCell>
                    <TableCell sx={{ width: 230 }}>{t('servicesTab.laborItemOptional')}</TableCell>
                    <TableCell sx={{ width: 88 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {laborItems.map((li) => (
                    <TableRow key={li.id}>
                      <TableCell>
                        <TextField
                          value={li.description}
                          onChange={(e) => handleLaborItemChange(li.id, 'description', e.target.value)}
                          fullWidth
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={li.estimatedHours}
                          onChange={(e) =>
                            handleLaborItemChange(li.id, 'estimatedHours', parseFloat(e.target.value) || 0)
                          }
                          size="small"
                          variant="outlined"
                          slotProps={{
                            htmlInput: { min: 0, step: 0.25 },
                            input: {
                              endAdornment: <InputAdornment position="end">h</InputAdornment>,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {/* A step that may be left out, and whether it starts ticked (#3347). */}
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={(
                            <Checkbox
                              size="small"
                              checked={li.isOptional}
                              onChange={(e) => handleLaborItemChange(li.id, 'isOptional', e.target.checked)}
                            />
                          )}
                          label={t('servicesTab.laborItemOptional')}
                        />
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={(
                            <Checkbox
                              size="small"
                              checked={li.selectedByDefault}
                              disabled={!li.isOptional}
                              onChange={(e) => handleLaborItemChange(li.id, 'selectedByDefault', e.target.checked)}
                            />
                          )}
                          label={t('servicesTab.laborItemSelectedByDefault')}
                        />
                      </TableCell>
                      <TableCell>
                        {editingService && UUID_RE.test(li.id) && (
                          <Tooltip title={t('servicesTab.vehicleTimesTitle', { name: li.description || t('servicesTab.laborItemDesc') })}>
                            <IconButton
                              size="small"
                              onClick={() => setTimesForItem(li)}
                              aria-label={t('servicesTab.vehicleTimesAria')}
                            >
                              <DirectionsCarIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={t('servicesTab.removeLaborItem')}>
                          <IconButton size="small" onClick={() => handleRemoveLaborItem(li.id)} color="error" aria-label={t('servicesTab.removeLaborItem')}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <Box>
              <Button startIcon={<AddIcon />} onClick={handleAddLaborItem} size="small">
                {t('servicesTab.addLaborItem')}
              </Button>
            </Box>
          </Stack>
        )}

        {activeTab === 'extras' && (
          <Stack spacing={3}>
            {/* Part Categories section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('servicesTab.partCategories')}
              </Typography>
              {/* What a category actually does (#3688): a placeholder part line, nothing more. */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="part-categories-help">
                {t('servicesTab.partCategoriesHelp')}
              </Typography>
              {partCategories.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('servicesTab.noPartCategories')}
                </Typography>
              ) : (
                <Table size="small" sx={{ mb: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('servicesTab.name')}</TableCell>
                      <TableCell sx={{ width: 80 }}>{t('servicesTab.partCategoryQuantity')}</TableCell>
                      <TableCell sx={{ width: 120 }}>
                        <Tooltip title={t('servicesTab.partCategoryRequiredInQuoteHint')}>
                          <span>{t('servicesTab.partCategoryRequiredInQuote')}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{t('servicesTab.partCategoryNotes')}</TableCell>
                      <TableCell sx={{ width: 50 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {partCategories.map((pc) => (
                      <TableRow key={pc.id}>
                        <TableCell>{pc.partCategoryName}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={pc.defaultQuantity}
                            onChange={(e) => {
                              // Whole pieces, as the catalog stores them (#3445).
                              const val = Number.parseInt(e.target.value, 10);
                              handlePartCategoryChange(pc.id, 'defaultQuantity', Number.isNaN(val) || val < 1 ? 1 : val);
                            }}
                            size="small"
                            variant="outlined"
                            slotProps={{ htmlInput: { min: 1, step: 1 } }}
                            sx={{ width: 70 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={pc.isRequired}
                            onChange={(e) => handlePartCategoryChange(pc.id, 'isRequired', e.target.checked)}
                            size="small"
                            slotProps={{ input: { 'aria-label': t('servicesTab.partCategoryRequiredInQuote') } }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            value={pc.notes}
                            onChange={(e) => handlePartCategoryChange(pc.id, 'notes', e.target.value)}
                            size="small"
                            variant="outlined"
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t('servicesTab.removePartCategory')}>
                            <IconButton size="small" onClick={() => handleRemovePartCategory(pc.id)} color="error" aria-label={t('servicesTab.removePartCategory')}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {availableCategories.length > 0 && (
                <Autocomplete
                  options={availableCategories}
                  getOptionLabel={(opt) => opt.name}
                  onChange={(_, value) => { if (value) handleAddPartCategory(value); }}
                  value={null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={t('servicesTab.selectPartCategory')}
                    />
                  )}
                  size="small"
                  sx={{ maxWidth: 400 }}
                />
              )}
            </Box>

            {/* Extras section */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('servicesTab.extras')}
              </Typography>
              {extras.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('servicesTab.noExtras')}
                </Typography>
              ) : (
                <Table size="small" sx={{ mb: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('servicesTab.extraDesc')}</TableCell>
                      <TableCell sx={{ width: 160 }}>{t('servicesTab.extraAmount')}</TableCell>
                      <TableCell sx={{ width: 80 }}>{t('servicesTab.extraTaxable')}</TableCell>
                      <TableCell sx={{ width: 80 }}>{t('servicesTab.extraVariableQty')}</TableCell>
                      <TableCell sx={{ width: 90 }}>{t('servicesTab.extraUnit')}</TableCell>
                      <TableCell sx={{ width: 50 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {extras.map((ex) => (
                      <TableRow key={ex.id}>
                        <TableCell>
                          <TextField
                            value={ex.description}
                            onChange={(e) => handleExtraChange(ex.id, 'description', e.target.value)}
                            fullWidth
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            value={ex.amountInput}
                            onChange={(e) => {
                              handleExtraChange(ex.id, 'amountInput', e.target.value);
                            }}
                            onBlur={() => {
                              const cents = eurosToCents(ex.amountInput) ?? 0;
                              setExtras((prev) =>
                                prev.map((item) =>
                                  item.id === ex.id
                                    ? { ...item, amountCents: cents, amountInput: (cents / 100).toFixed(2) }
                                    : item,
                                ),
                              );
                            }}
                            size="small"
                            variant="outlined"
                            slotProps={{
                              input: { endAdornment: <InputAdornment position="end">{ex.hasVariableQuantity ? `€/${ex.unit || '?'}` : '€'}</InputAdornment> },
                              htmlInput: { min: 0, step: '0.01' },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={ex.isTaxable}
                            onChange={(e) => handleExtraChange(ex.id, 'isTaxable', e.target.checked)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Checkbox
                            checked={ex.hasVariableQuantity}
                            onChange={(e) => handleExtraChange(ex.id, 'hasVariableQuantity', e.target.checked)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {ex.hasVariableQuantity && (
                            <TextField
                              value={ex.unit}
                              onChange={(e) => handleExtraChange(ex.id, 'unit', e.target.value)}
                              size="small"
                              variant="outlined"
                              placeholder={t('servicesTab.extraUnitPlaceholder')}
                              sx={{ width: 80 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={t('servicesTab.removeExtra')}>
                            <IconButton size="small" onClick={() => handleRemoveExtra(ex.id)} color="error" aria-label={t('servicesTab.removeExtra')}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Button startIcon={<AddIcon />} onClick={handleAddExtra} size="small">
                {t('servicesTab.addExtra')}
              </Button>
            </Box>
          </Stack>
        )}

        {activeTab === 'skills' && (
          <Stack spacing={2}>
            {skills.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('servicesTab.noSkills')}
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('servicesTab.name')}</TableCell>
                    <TableCell sx={{ width: 160 }}>{t('servicesTab.proficiency')}</TableCell>
                    <TableCell sx={{ width: 80 }}>{t('servicesTab.required')}</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {skills.map((sk) => (
                    <TableRow key={sk.id}>
                      <TableCell>{sk.skillName}</TableCell>
                      <TableCell>
                        <Select
                          value={sk.minimumProficiency}
                          onChange={(e) => handleSkillChange(sk.id, 'minimumProficiency', e.target.value)}
                          size="small"
                          variant="outlined"
                          fullWidth
                        >
                          {proficiencyOptions.map((level) => (
                            <MenuItem key={level} value={level}>
                              {proficiencyLabel(level)}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={sk.isRequired}
                          onChange={(e) => handleSkillChange(sk.id, 'isRequired', e.target.checked)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t('servicesTab.removeSkill')}>
                          <IconButton size="small" onClick={() => handleRemoveSkill(sk.id)} color="error" aria-label={t('servicesTab.removeSkill')}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {availableSkills.length > 0 && (
              <Autocomplete
                options={availableSkills}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                groupBy={(opt) => t(skillTypeGroupKey(opt.type))}
                onChange={(_, value) => { if (value) handleAddSkill(value); }}
                value={null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={t('servicesTab.selectSkill')}
                    helperText={t('servicesTab.selectSkillHint')}
                  />
                )}
                size="small"
                sx={{ maxWidth: 400 }}
              />
            )}
          </Stack>
        )}

        {activeTab === 'equipment' && (
          <Stack spacing={2}>
            {lifts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('servicesTab.noEquipment')}
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('lifts.type')}</TableCell>
                    <TableCell sx={{ width: 80 }}>{t('servicesTab.liftRequired')}</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lifts.map((lt) => (
                    <TableRow key={lt.id}>
                      <TableCell>{liftTypeLabel(lt.liftType)}</TableCell>
                      <TableCell>
                        <Checkbox
                          checked={lt.isRequired}
                          onChange={(e) => handleLiftChange(lt.id, 'isRequired', e.target.checked)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t('servicesTab.removeLift')}>
                          <IconButton size="small" onClick={() => handleRemoveLift(lt.id)} color="error" aria-label={t('servicesTab.removeLift')}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {availableLiftTypes.length > 0 && (
              <Autocomplete
                options={availableLiftTypes}
                getOptionLabel={(opt) => t(opt.labelKey)}
                onChange={(_, value) => { if (value) handleAddLift(value); }}
                value={null}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder={t('servicesTab.selectLiftType')}
                  />
                )}
                size="small"
                sx={{ maxWidth: 400 }}
              />
            )}
          </Stack>
        )}
      </DialogContent>

    </FormDialog>
      {editingService && timesForItem && (
        <LaborItemVehicleTimesDialog
          open
          onClose={() => setTimesForItem(null)}
          serviceId={editingService.id}
          laborItemId={timesForItem.id}
          laborItemDescription={timesForItem.description}
          times={timesForItem.vehicleTimes}
        />
      )}
    </>
  );
}
